/* BergHOFF V5 — product.js
 * Variant selection, media gallery, quantity, sticky mobile add-to-cart and lazy
 * recommendations. The page is fully usable without it (noscript variant select,
 * scrollable gallery, normal form post).
 */
(() => {
  'use strict';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Gallery ---------- */
  const initGallery = (gallery) => {
    const track = qs('[data-gallery-track]', gallery);
    if (!track) return;
    const slides = qsa('[data-media-id]', track);
    const thumbs = qsa('[data-gallery-thumb]', gallery);
    const indexEl = qs('[data-gallery-index]', gallery);
    const prev = qs('[data-gallery-prev]', gallery);
    const next = qs('[data-gallery-next]', gallery);
    let current = 0;

    const setCurrent = (i) => {
      current = i;
      if (indexEl) indexEl.textContent = String(i + 1);
      thumbs.forEach((t, ti) => t.setAttribute('aria-current', String(ti === i)));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };

    const goTo = (i, smooth = true) => {
      const slide = slides[i];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setCurrent(i);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) setCurrent(slides.indexOf(entry.target));
      });
    }, { root: track, threshold: [0.6] });
    slides.forEach((s) => observer.observe(s));

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    prev?.addEventListener('click', () => goTo(Math.max(0, current - 1)));
    next?.addEventListener('click', () => goTo(Math.min(slides.length - 1, current + 1)));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, current + 1)); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, current - 1)); }
    });

    // Deferred video / 3D: load the player only when asked.
    qsa('[data-deferred-media]', gallery).forEach((btn) => {
      btn.addEventListener('click', () => {
        const tpl = btn.nextElementSibling;
        if (!tpl || tpl.tagName !== 'TEMPLATE') return;
        btn.replaceWith(tpl.content.cloneNode(true));
      });
    });

    const initial = gallery.dataset.initialMedia;
    const initialIndex = slides.findIndex((s) => s.dataset.mediaId === initial);
    if (initialIndex > 0) goTo(initialIndex, false); else setCurrent(0);

    gallery.showMedia = (mediaId) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) goTo(i);
    };
  };

  /* ---------- Product section ---------- */
  const initProduct = (section) => {
    const dataEl = qs('[data-product-json]', section);
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const gallery = qs('[data-gallery]', section);
    if (gallery) initGallery(gallery);

    const form = qs('[data-product-form]', section);
    const idInput = qs('[data-variant-id]', section);
    if (idInput) idInput.disabled = false;
    const atc = qs('[data-atc]', section);
    const sticky = qs('[data-sticky-atc]', section);
    const picker = qs('[data-variant-picker]', section);

    /* Quantity */
    const qtyInput = form ? qs('input[name="quantity"]', form) : null;
    qs('[data-qty-minus]', section)?.addEventListener('click', () => {
      if (qtyInput) qtyInput.value = String(Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1));
      qtyInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });
    qs('[data-qty-plus]', section)?.addEventListener('click', () => {
      if (qtyInput) qtyInput.value = String((parseInt(qtyInput.value, 10) || 1) + 1);
      qtyInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });

    /* Variants */
    const selectedOptions = () => qsa('fieldset[data-option-position]', picker || section).map((fs) => {
      const checked = qs('input[data-option-input]:checked', fs);
      return checked ? checked.value : null;
    });
    const findVariant = (options) => data.variants.find((v) => v.options.every((o, i) => o === options[i]));

    const updateOptionAvailability = (options) => {
      qsa('fieldset[data-option-position]', picker).forEach((fs, pos) => {
        qsa('input[data-option-input]', fs).forEach((input) => {
          const test = options.slice();
          test[pos] = input.value;
          const match = findVariant(test);
          const available = Boolean(match && match.available);
          const label = input.nextElementSibling;
          label?.classList.toggle('is-unavailable', !available);
          const sr = label && qs('[data-unavailable-text]', label);
          if (sr) sr.hidden = available;
        });
        const selectedLabel = qs('[data-option-selected]', fs);
        if (selectedLabel) selectedLabel.textContent = options[pos] || '';
      });
    };

    const setButtons = (variant) => {
      const available = Boolean(variant && variant.available);
      const label = !variant ? data.strings.unavailable : (available ? data.strings.addToCart : data.strings.soldOut);
      [atc, qs('[data-sticky-atc-button]', section)].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !available;
        const l = qs('[data-atc-label]', btn);
        if (l) l.textContent = label;
      });
      const atcPrice = qs('[data-atc-price]', section);
      if (atcPrice) atcPrice.textContent = variant ? `· ${variant.price}` : '';
    };

    const setPrice = (variant) => {
      const block = qs('[data-product-price]', section);
      if (!block || !variant) return;
      const current = qs('[data-price-current]', block);
      const compare = qs('[data-price-compare]', block);
      if (current) current.textContent = variant.price;
      if (compare) {
        compare.textContent = variant.compare || '';
        compare.hidden = !variant.compare;
      }
      qsa('[data-price-sale-label], [data-price-regular-label]', block).forEach((el) => {
        el.hidden = !variant.compare;
        el.textContent = variant.compare ? el.dataset.label || '' : '';
      });
      qs('.price', block)?.classList.toggle('price--sale', Boolean(variant.compare));
      const badge = qs('[data-gallery-sale-badge]', section);
      if (badge) {
        badge.hidden = !variant.badge;
        const t = qs('[data-discount-text]', badge);
        if (t) t.textContent = variant.badge || '';
      }
    };

    const setAvailability = (variant) => {
      const el = qs('[data-availability]', section);
      if (!el) return;
      const state = !variant ? 'out' : variant.stock;
      el.className = `stock${state === 'out' ? ' stock--out' : state === 'backorder' ? ' stock--backorder' : ''}`;
      el.textContent = !variant ? data.strings.unavailable : state === 'out' ? data.strings.soldOut : state === 'backorder' ? data.strings.backorder : data.strings.inStock;
    };

    const setSticky = (variant) => {
      if (!sticky || !variant) return;
      const price = qs('[data-sticky-price]', sticky);
      if (price) {
        const title = variant.title && variant.title !== 'Default Title' ? ` · ${variant.title}` : '';
        price.textContent = `${variant.price}${title}`;
      }
    };

    // Reconcile price-related markup with the server (unit price, tax and Omnibus notes).
    let refreshController;
    const refreshFromServer = async (variant) => {
      refreshController?.abort();
      refreshController = new AbortController();
      try {
        const url = `${data.url}?variant=${variant.id}&section_id=${section.dataset.sectionId}`;
        const res = await fetch(url, { signal: refreshController.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        ['[data-product-price]', '[data-availability]'].forEach((sel) => {
          const fresh = qs(sel, doc);
          const current = qs(sel, section);
          if (fresh && current) current.replaceWith(fresh);
        });
      } catch (e) { /* keep the instant JSON update */ }
    };

    const onVariantChange = () => {
      const options = selectedOptions();
      const variant = findVariant(options);
      updateOptionAvailability(options);
      setButtons(variant);
      setAvailability(variant);
      if (!variant) return;
      if (idInput) idInput.value = String(variant.id);
      setPrice(variant);
      setSticky(variant);
      if (variant.media && gallery?.showMedia) gallery.showMedia(variant.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      refreshFromServer(variant);
    };

    if (picker) {
      picker.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) onVariantChange();
      });
      updateOptionAvailability(selectedOptions());
    }

    /* Sticky add to cart (shown once the main button has scrolled above the viewport) */
    if (sticky && atc && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !show;
        document.body.classList.toggle('has-sticky-atc', show);
      });
      io.observe(atc);
    }
  };

  /* ---------- Lazy recommendations ---------- */
  const initRecommendations = (el) => {
    const load = async () => {
      try {
        const res = await fetch(el.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = qs('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          el.classList.remove('recommendations__placeholder');
        } else {
          el.closest('section')?.remove();
        }
      } catch (e) { el.closest('section')?.remove(); }
    };
    const io = new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) { obs.disconnect(); load(); }
    }, { rootMargin: '400px 0px' });
    io.observe(el);
  };

  const init = () => {
    qsa('[data-product]').forEach(initProduct);
    qsa('[data-recommendations][data-url]').forEach(initRecommendations);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
